import { useEffect, useCallback, useRef, useState } from 'react';

/**
 * Custom hook for intelligent data refresh
 * - Refetches on window focus (when user returns to tab)
 * - Optional periodic refresh interval
 * - Prevents rapid repeated fetches
 */
export function useDataRefresh(fetchFunction, options = {}) {
  const {
    refreshOnFocus = true,
    refreshInterval = null, // in milliseconds, null = disabled
    minRefreshGap = 5000, // minimum 5 seconds between refreshes
  } = options;

  const lastFetchTime = useRef(0);
  const [lastUpdated, setLastUpdated] = useState(null);

  const refresh = useCallback(async () => {
    const now = Date.now();
    // Prevent rapid repeated fetches
    if (now - lastFetchTime.current < minRefreshGap) {
      return;
    }
    
    lastFetchTime.current = now;
    try {
      await fetchFunction();
      setLastUpdated(new Date());
    } catch (error) {
      console.error('Data refresh failed:', error);
    }
  }, [fetchFunction, minRefreshGap]);

  // Refresh on window focus
  useEffect(() => {
    if (!refreshOnFocus) return;

    const handleFocus = () => {
      // Only refresh if tab was inactive for more than minRefreshGap
      refresh();
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        refresh();
      }
    };

    window.addEventListener('focus', handleFocus);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      window.removeEventListener('focus', handleFocus);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [refresh, refreshOnFocus]);

  // Periodic refresh interval
  useEffect(() => {
    if (!refreshInterval) return;

    const intervalId = setInterval(refresh, refreshInterval);
    return () => clearInterval(intervalId);
  }, [refresh, refreshInterval]);

  return { refresh, lastUpdated };
}

/**
 * Format relative time (e.g., "2 minutes ago")
 */
export function formatLastUpdated(date) {
  if (!date) return '';
  
  const now = new Date();
  const diffMs = now - date;
  const diffSecs = Math.floor(diffMs / 1000);
  const diffMins = Math.floor(diffSecs / 60);
  const diffHours = Math.floor(diffMins / 60);

  if (diffSecs < 10) return 'Just now';
  if (diffSecs < 60) return `${diffSecs}s ago`;
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  return date.toLocaleDateString();
}
